import { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { TrendingUp } from "lucide-react";
import { api } from "@/lib/api";

function buildLast7Days(daily = {}) {
  const days = [];
  const now = new Date();
  for (let i = 6; i >= 0; i--) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() - i);
    const key = d.toISOString().slice(0, 10);
    days.push({
      date: key,
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
      count: daily[key] || 0,
    });
  }
  return days;
}

export default function UsageChart() {
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);

  useEffect(() => {
    let cancel = false;
    api.get("/stats").then(({ data: stats }) => {
      if (cancel) return;
      const rows = buildLast7Days(stats.daily || {});
      setData(rows);
      setTotal(rows.reduce((s, r) => s + r.count, 0));
    }).catch(() => {});
    return () => { cancel = true; };
  }, []);

  const peak = Math.max(...data.map((d) => d.count), 0);

  return (
    <div className="rounded-xl border border-slate-800 bg-slate-900/60 p-6" data-testid="usage-chart">
      <div className="flex items-start justify-between mb-4">
        <div>
          <h3 className="font-display font-semibold text-lg flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-cyan-signal" /> Usage · last 7 days
          </h3>
          <p className="text-sm text-slate-500 mt-0.5">
            <span className="font-mono text-slate-300">{total.toLocaleString()}</span> verifications ·
            <span className="font-mono text-slate-300 ml-1">{peak.toLocaleString()}</span> peak day
          </p>
        </div>
      </div>
      <div className="h-40 min-h-[160px]">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 4, left: -18, bottom: 0 }}>
            <defs>
              <linearGradient id="usageFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%"   stopColor="#00f0ff" stopOpacity={0.5} />
                <stop offset="100%" stopColor="#00f0ff" stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#1e293b" strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="label" stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} />
            <YAxis stroke="#64748b" fontSize={11} tickLine={false} axisLine={false} width={36} allowDecimals={false} />
            <Tooltip
              cursor={{ stroke: "#00f0ff", strokeOpacity: 0.2 }}
              contentStyle={{ background: "#0a0d14", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }}
              labelStyle={{ color: "#94a3b8" }}
              itemStyle={{ color: "#00f0ff" }}
              formatter={(v) => [v, "lookups"]}
            />
            <Area type="monotone" dataKey="count" stroke="#00f0ff" strokeWidth={2} fill="url(#usageFill)" />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
